import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openCookieSettings } from "@/components/site/CookieBanner";
import { useLang } from "@/i18n/LangContext";
import logo from "@/assets/logo.svg";

export const Footer = () => {
  const { t, prefix, lang } = useLang();
  const sv = lang === "sv";

  const bookingHref = sv ? "/boka-tid" : "/en/book-appointment";

  const serviceLinks = [
    { to: sv ? "/lakarintyg/hogre-behorighet" : "/en/certificates/hogre-behorighet", label: sv ? "Högre körkortsbehörighet" : "Higher driving licence" },
    { to: sv ? "/lakarintyg/alkolas" : "/en/certificates/alkolas", label: sv ? "Alkolåsintyg" : "Alcohol interlock" },
    { to: sv ? "/lakarintyg/sjofolksintyg" : "/en/certificates/sjofolksintyg", label: sv ? "Sjöfolksintyg" : "Seafarer medicals" },
    { to: `${prefix}/lakarintyg`, label: sv ? "Alla läkarintyg" : "All certificates" },
    { to: sv ? "/drogtestning" : "/en/drug-testing", label: sv ? "Privat drogtestning" : "Private drug testing" },
    { to: sv ? "/provtagning" : "/en/sample-collection", label: sv ? "Provtagning nära dig" : "Sample collection near you" },
    { to: sv ? "/foretag" : "/en/companies", label: sv ? "För företag" : "For businesses" },
    { to: sv ? "/foretag/drogtestning" : "/en/companies/drug-testing", label: sv ? "Drogtestning för företag" : "Business drug testing" },
  ];

  const infoLinks = [
    { to: sv ? "/starta-arende" : "/en/start-case", label: sv ? "Starta ärende" : "Start case" },
    { to: sv ? "/sa-fungerar-det" : "/en/how-it-works", label: sv ? "Så fungerar det" : "How it works" },
    { to: sv ? "/guider" : "/en/guides", label: sv ? "Guider" : "Guides" },
    { to: sv ? "/infor-besoket" : "/en/before-visit", label: t.nav.visit },
    { to: sv ? "/fragor-och-svar" : "/en/faq", label: t.nav.faq },
    { to: sv ? "/halsokontroller" : "/en/health-checks", label: t.nav.checkups },
    { to: sv ? "/om-oss" : "/en/about-us", label: t.about.eyebrow },
    { to: sv ? "/kontakt" : "/en/contact", label: t.nav.contact },
  ];

  const legalLinks = [
    { to: sv ? "/integritetspolicy" : "/en/privacy-policy", label: t.footer.privacy },
    { to: sv ? "/cookie-policy" : "/en/cookie-policy", label: t.footer.cookies },
    { to: sv ? "/anvandarvillkor" : "/en/terms-of-use", label: t.footer.terms },
  ];

  return (
    <footer id="site-footer" className="rounded-t-2xl bg-primary text-primary-foreground sm:rounded-t-3xl">
      {/* Main footer body */}
      <div className="container-wide py-14 lg:py-20">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">

          {/* Brand column */}
          <div className="flex flex-col gap-6">
            <Link to={`${prefix}/`} aria-label="Intygsläkaren">
              <img
                src={logo}
                alt="Intygsläkaren"
                width={168}
                height={40}
                className="logo-white h-10 w-auto"
              />
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-primary-foreground/80">
              {sv
                ? "Legitimerade läkare, mottagning i Göteborg och videobesök i hela Sverige när intygets krav tillåter."
                : "Licensed doctors, a clinic in Gothenburg and video appointments across Sweden when the certificate's requirements allow."}
            </p>
            <p className="text-xs text-primary-foreground/70">
              AG Medical AB · 559282-3685
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="onPrimary" size="default">
                <a href={bookingHref}>
                  {sv ? "Boka tid" : "Book appointment"}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>

          {/* Tjänster */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/70">
              {sv ? "Tjänster" : "Services"}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {serviceLinks.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Intygsläkaren info pages */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/70">
              Intygsläkaren
            </h2>
            <ul className="mt-4 space-y-2.5">
              {infoLinks.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Visit & contact */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/70">
              {sv ? "Besök och kontakt" : "Visit and contact"}
            </h2>
            <ul className="mt-4 space-y-3.5 text-sm">
              <li className="flex items-start gap-2.5 text-primary-foreground/80">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary-foreground/70" aria-hidden />
                <span>{sv ? "Järntorgsgatan 8, 413 30 Göteborg" : "Järntorgsgatan 8, 413 30 Gothenburg, Sweden"}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-primary-foreground/70" aria-hidden />
                <a href="tel:+46317617700" className="text-primary-foreground/80 hover:text-primary-foreground">
                  {sv ? "031-761 77 00" : "+46 31 761 77 00"}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-primary-foreground/70" aria-hidden />
                <a href="mailto:info@intygslakaren.se" className="text-primary-foreground/80 hover:text-primary-foreground">
                  info@intygslakaren.se
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-primary-foreground/80">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary-foreground/70" aria-hidden />
                <span>
                  {sv ? (
                    <>Mån–Fre 08:00–17:00<br />Lör–Sön Stängt</>
                  ) : (
                    <>Mon–Fri 08:00–17:00<br />Sat–Sun Closed</>
                  )}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-primary-foreground/15">
        <div className="container-wide flex flex-col items-start justify-between gap-3 py-5 text-xs text-primary-foreground/70 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Intygsläkaren · {sv ? "Alla rättigheter förbehållna." : "All rights reserved."}</p>
          <nav className="flex flex-wrap items-center gap-4" aria-label={sv ? "Policyer" : "Policies"}>
            {legalLinks.map((item) => (
              <Link key={item.to} to={item.to} className="hover:text-primary-foreground">
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openCookieSettings}
              className="hover:text-primary-foreground"
            >
              {t.footer.manageCookies}
            </button>
          </nav>
          <p>
            {sv ? "Skapad av" : "Created by"}{" "}
            <a href="https://fahmedia.com" target="_blank" rel="noopener" className="hover:text-primary-foreground">
              FAH Media
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
